/**
 * Hook consolidado para sincronização realtime de produção
 * Consolida: useOrdersSync + useProductionStepsSync + useEventsSync + useObservationsSync
 * Otimização v2.1: 4 hooks → 1 channel (economia de 75% em WebSocket connections)
 */

import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { realtime } from '@/lib/realtime';
import { useNotificationSound } from '@/hooks/useNotificationSound';
import { debouncedInvalidateProductionQueries } from '@/utils/queryInvalidation';
import { invalidateOccurrenceQueries } from '@/hooks/useOccurrences';
import { invalidateStockQueries } from '@/hooks/useStock';
import { inboundKeys, pendingKeys } from '@/hooks/useStockInbound';
import { demandKeys } from '@/hooks/useStockDemands';

export const useProductionCoreSync = () => {
  const queryClient = useQueryClient();
  const { playSound } = useNotificationSound();
  
  // Map para controle de firstRender por tabela (evita sons na carga inicial)
  const firstRenderMap = useRef(new Map([
    ['orders', true],
    ['production_steps', true],
    ['production_step_events', true],
    ['observacoes_multimidia', true]
  ]));

  const shouldSkipSound = (table: string): boolean => {
    if (firstRenderMap.current.get(table)) {
      firstRenderMap.current.set(table, false);
      return true;
    }
    return false;
  };

  useEffect(() => {
    // Walter 2026-05-17: removido guard `isSystemActive()`. Sleep mode era pra
    // economizar conexões Supabase fora do horário comercial, mas migramos pra
    // Socket.io self-hosted e equipe usa o sistema aos domingos (descoberto via
    // Walter testando GO LIVE). Sem isso, todos os hooks realtime saíam silenciosamente
    // e usuário via app dessincronizado sem F5. Guard de `document.hidden` no
    // useRealtimeSync continua pausando heartbeat em aba background.
    console.log('🔔 Production Core Sync: Inicializando channel consolidado (15 listeners)');

    // Reset do Map após delay inicial
    const resetTimeout = setTimeout(() => {
      firstRenderMap.current.forEach((_, key) => {
        firstRenderMap.current.set(key, false);
      });
    }, 2000);

    const channel = realtime
      .channel('production-core-sync')
      
      // Listener 1: Orders
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: 'orders' },
        async (payload) => {
          console.log('🔔 Production Core: orders mudou', payload.eventType);
          debouncedInvalidateProductionQueries(queryClient, { immediate: true });
          queryClient.invalidateQueries({ queryKey: ['painel'] });
          queryClient.invalidateQueries({ queryKey: ['integrity-alerts'] });
          // HOTFIX 3 (Walter 2026-05-18): removido refetchQueries({type:'active'}) — o que
          // havia sido adicionado em d638362e ("paridade Lovable") combinado com remoção
          // do sleep mode (b45f288) causou storm de refetch em segunda 18/Mai: cada UPDATE
          // em orders disparava refetch de 750KB × N abas × N hooks = CPU 103%. Bug original
          // (painel não atualiza sem F5) deve ser resolvido via staleTime menor + invalidate
          // em vez de refetch forçado — investigar fora da crise.

          // #1997: removida a reconciliação automática de `EM_EXPEDICAO` — esse
          // status nunca existiu em `orders.status` em produção (medição
          // 13/05–27/09/2026, CLAUDE.md §8: só CONCLUIDO/PRONTO/EM_PRODUCAO/
          // PENDENTE_PROGRAMACAO) e nenhuma rota grava esse valor. O listener
          // nunca disparava; a coluna `last_reconciliation_attempt` segue no
          // banco (não se remove coluna), só o código morto saiu.
          if (payload.eventType === 'UPDATE') {
            const newOrder = payload.new as { prioridade?: string };
            const oldOrder = payload.old as { prioridade?: string };

            // Som para prioridade urgente
            if (!shouldSkipSound('orders')) {
              if (oldOrder.prioridade !== 'URGENTE' && newOrder.prioridade === 'URGENTE') {
                playSound('urgent');
              }
            }
          }
          
          if (shouldSkipSound('orders')) return;
          
          // Som para novo pedido pendente
          if (payload.eventType === 'INSERT') {
            const newOrder = payload.new as { status?: string };
            if (newOrder.status === 'PENDENTE_PROGRAMACAO') {
              playSound('new_order');
            }
          }
        }
      )
      
      // Listener 2: Production Steps
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'production_steps' },
        (payload) => {
          console.log('🔔 Production Core: production_steps mudou', payload.eventType);
          debouncedInvalidateProductionQueries(queryClient, {
            orderSteps: true,
            operatorTasks: true,
            immediate: true
          });
          queryClient.invalidateQueries({ queryKey: ['painel'] });
          queryClient.invalidateQueries({ queryKey: ['integrity-alerts'] });
          // HOTFIX 3 (Walter 2026-05-18): refetchQueries forçado removido — ver comentário
          // no listener de orders acima. Mesma regressão d638362e.
          
          // ✅ OTIMIZAÇÃO v2.2: Invalidar pré-requisitos do order específico
          // Isso substitui os canais individuais por card em OperatorExpedicaoCard
          const orderId = (payload.new as { order_id?: string })?.order_id || 
                         (payload.old as { order_id?: string })?.order_id;
          if (orderId) {
            queryClient.invalidateQueries({ 
              queryKey: ['order-prereq', orderId] 
            });
          }
          
          // ⚠️ A conclusão do corte/saída passa pelo adapter, e o TRIGGER
          // (0085/0090) cria a pendência de estoque sem nenhum emit próprio:
          // o badge do sidebar e a fila ficavam velhos até alguém navegar.
          // Só invalida (nunca refetch forçado — storm de 18/mai).
          if (payload.eventType === 'UPDATE') {
            const novo = payload.new as { status?: string; setor?: string };
            const velho = payload.old as { status?: string };
            if (novo.status === 'CONCLUIDO' && velho.status !== 'CONCLUIDO'
                && (novo.setor === 'CORTE' || novo.setor === 'SAIDA_ESTOQUE')) {
              queryClient.invalidateQueries({ queryKey: pendingKeys.all });
              queryClient.invalidateQueries({ queryKey: ['stock-alerts'] });
            }
          }

          if (shouldSkipSound('production_steps')) return;
          
          // Som para step concluído
          if (payload.eventType === 'UPDATE') {
            const oldStep = payload.old as { status?: string };
            const newStep = payload.new as { status?: string };
            if (oldStep.status !== 'CONCLUIDO' && newStep.status === 'CONCLUIDO') {
              playSound('completed');
            }
          }
        }
      )
      
      // Listener 3: Production Events
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'production_step_events' },
        (payload) => {
          console.log('🔔 Production Core: events mudou', payload.eventType);
          // A chave real é 'production_step_events' (useProductionStepEvents.ts; o
          // 'today' também vive sob ela). Com hífen a invalidação não achava nada.
          queryClient.invalidateQueries({ queryKey: ['production_step_events'] });
        }
      )
      
      // Listener 4: Observations
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'observacoes_multimidia' },
        (payload) => {
          console.log('🔔 Production Core: observacoes mudou', payload.eventType);
          queryClient.invalidateQueries({
            queryKey: ['observacoes'],
            refetchType: 'all'
          });
        }
      )

      // Listener 5: Ocorrências de retrabalho (0079)
      // O servidor já emitia este evento desde a 0075 e NINGUÉM escutava. Isso
      // não incomodava enquanto retrabalho era coisa de um gerente só; passou a
      // incomodar quando o registro virou trabalho de DUAS pessoas: o comercial
      // abre a ocorrência com a data prometida, e o aviso "Retrabalhos a
      // programar" tem de acender no quadro da PRODUÇÃO — outro navegador, que
      // costuma ficar aberto o dia inteiro sem receber foco. Sem este listener
      // o aviso só aparecia depois de um F5.
      //
      // Só invalida (nunca refetch forçado): é a lição do storm de 18/Mai
      // registrada nos listeners de orders e production_steps acima.
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'order_occurrences' },
        (payload) => {
          console.log('🔔 Production Core: order_occurrences mudou', payload.eventType);
          const orderId = (payload.new as { order_id?: string })?.order_id
            || (payload.old as { order_id?: string })?.order_id;
          invalidateOccurrenceQueries(queryClient, orderId);
        }
      )

      // Listener 6: Arte Job Events (histórico no modal)
      // Walter 2026-05-18 — revisão realtime #0: queryKey ['arte-job-events'] estava
      // sem invalidação automática. Modal de detalhes do arte_job mostrava histórico
      // desatualizado (cancelamento/reativação/evento) sem F5.
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'arte_job_events' },
        (payload) => {
          console.log('🔔 Production Core: arte_job_events mudou', payload.eventType);
          queryClient.invalidateQueries({ queryKey: ['arte-job-events'] });
        }
      )

      // Listeners 7-9: Estoque (0080)
      //
      // Entram NESTE channel, e não num `stock-sync` próprio, porque cada
      // `realtime.channel(nome)` abre um socket por aba: um canal novo custaria
      // +1 WebSocket em toda aba aberta do sistema, o dia inteiro.
      //
      // As três tabelas são GLOBAIS (estoque é físico e central, sem
      // `tenant_id`), então o emit alcança todos os conectados e o payload
      // carrega só ids — nunca dado de estoque.
      //
      // Por que os três eventos chamam a MESMA invalidação: um lançamento
      // qualquer mexe em saldo, movimentos e rolo ao mesmo tempo, e a tela do
      // posto e a do gerente leem as mesmas chaves. Discriminar por tabela só
      // criaria a chance de esquecer uma. Só invalida (storm de 18/mai).
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'stock_ledger' },
        (payload) => {
          console.log('🔔 Production Core: stock_ledger mudou', payload.eventType);
          invalidateStockQueries(queryClient);
        }
      )
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'stock_units' },
        (payload) => {
          console.log('🔔 Production Core: stock_units mudou', payload.eventType);
          invalidateStockQueries(queryClient);
        }
      )
      // O cabeçalho da devolução ao fornecedor (§13.106): abrir uma e ligar a NF não passam pelo ledger.
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'stock_devolucoes_fornecedor' },
        () => queryClient.invalidateQueries({ queryKey: ['stock', 'devolucoes'] })
      )
      // `materials` junto dos outros dois: o cadastro é trabalho de gerente no
      // escritório e o consumo é do estoquista no posto — sem este listener o
      // material recém-criado só apareceria no combobox do posto após F5.
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'materials' },
        (payload) => {
          console.log('🔔 Production Core: materials mudou', payload.eventType);
          invalidateStockQueries(queryClient);
        }
      )

      // Listeners 10-11: Recebimento de MP (Bloco A, 0083)
      //
      // Sem eles, o wizard aberto num PC não veria a conferência que outra
      // pessoa acabou de concluir, e a fila de pendências do gerente só
      // acenderia depois de um F5 — a mesma falha que order_occurrences teve
      // da 0075 até a 0079.
      //
      // Chaves SEPARADAS das de saldo de propósito: uma pendência resolvida
      // não muda saldo nenhum, e invalidar tudo faria a tela de saldos
      // refetchar à toa a cada ação na fila.
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'inbound_receipts' },
        (payload) => {
          console.log('🔔 Production Core: inbound_receipts mudou', payload.eventType);
          queryClient.invalidateQueries({ queryKey: inboundKeys.all });
        }
      )
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'stock_pending_tasks' },
        (payload) => {
          console.log('🔔 Production Core: stock_pending_tasks mudou', payload.eventType);
          queryClient.invalidateQueries({ queryKey: pendingKeys.all });
          // O painel da separação (`['stock','saida-pecas',etapa]`) mostra a falta e a decisão
          // do gerente (linha "sai do corte"): sem isto a operadora com o diálogo aberto seguia
          // vendo "falta registrada" depois do "Cortar" e tentava lançar a peça de novo.
          queryClient.invalidateQueries({ queryKey: ['stock', 'saida-pecas'] });
          // A contagem do AlertsSidebar vive FORA do prefixo ['stock'] de
          // propósito (não pode refetchar a cada bipe) — então é invalidada
          // aqui, pelo único evento que a muda.
          queryClient.invalidateQueries({ queryKey: ['stock-alerts'] });
        }
      )
      // A fila de chegadas do posto fica aberta o dia inteiro; o poller do GC
      // (15 min) emite quando traz documento novo. Sem este listener a NF só
      // aparecia quando outro evento de estoque invalidasse por acaso.
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'gc_compras_entrada' },
        () => {
          queryClient.invalidateQueries({ queryKey: inboundKeys.all });
        }
      )

      // Listeners 12-13: Reservas e demandas (Bloco B, 0085)
      //
      // As rotas já emitiam os dois e NINGUÉM escutava — a repetição literal do
      // bug de `order_occurrences`, que o §7.5 do CLAUDE.md registra e que este
      // módulo dizia ter nascido evitando. Sem eles, dois compradores na mesma
      // fila não se enxergam, e a reserva feita numa aba não muda o
      // "disponível" de ninguém.
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'stock_reservations' },
        (payload) => {
          console.log('🔔 Production Core: stock_reservations mudou', payload.eventType);
          queryClient.invalidateQueries({ queryKey: demandKeys.all });
          queryClient.invalidateQueries({ queryKey: ['stock', 'order-materials'] });
          // A reserva é o "tem / reservado" de cada linha da separação.
          queryClient.invalidateQueries({ queryKey: ['stock', 'saida-pecas'] });
          // Reserva muda o DISPONÍVEL, que é o número grande da tela de saldos.
          queryClient.invalidateQueries({ queryKey: ['stock', 'balances'] });
          // "Reservado para quem?" (§13.96): a lista por pedido muda junto com a reserva.
          queryClient.invalidateQueries({ queryKey: ['stock', 'reservas-do-material'] });
        }
      )
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'purchase_demands' },
        (payload) => {
          console.log('🔔 Production Core: purchase_demands mudou', payload.eventType);
          queryClient.invalidateQueries({ queryKey: demandKeys.all });
          // O contador de compras atrasadas do painel do gerente (fora de ['stock']).
          queryClient.invalidateQueries({ queryKey: ['stock-alerts', 'compras-atrasadas'] });
          // A fila do Recebimento lista as compras feitas no sistema ("Comprei" de outro usuário).
          queryClient.invalidateQueries({ queryKey: inboundKeys.filaSistema });
          // O card do corte deriva "reservado / a caminho / recebido" do status
          // da demanda — sem isto ficava "reservado" depois de "em compra".
          queryClient.invalidateQueries({ queryKey: ['stock', 'order-materials'] });
        }
      )

      // Listener 14: Pacotes de peças lisas (Bloco C, 0087)
      //
      // O pacote é o objeto físico das lisas: criar, abater na saída e dar
      // entrada na produção mexem no saldo e no histórico bipável ao mesmo
      // tempo — a mesma invalidação dos rolos, pela mesma razão.
      //
      // Sem ele, o mutirão do cutover com duas pessoas etiquetando lado a lado
      // não veria os pacotes um do outro, e a lista de "reimprimir as etiquetas
      // deste pedido" ficaria velha na tela de quem não criou.
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'stock_packages' },
        (payload) => {
          console.log('🔔 Production Core: stock_packages mudou', payload.eventType);
          invalidateStockQueries(queryClient);
        }
      )

      // Listener 15: Itens projetados do pedido (o QUE produzir)
      //
      // `order_metadata_items` é o que o gerente lê para programar e o que o
      // operador lê no setor — código, modelo, tecido, cor, grade e total. Ele
      // é reprojetado toda vez que alguém salva o card comercial, e o emit vem
      // da RPC `project_order_metadata_items` (routes/db.ts), porque função SQL
      // não passa pelo broadcast das mutations (§7.5).
      //
      // O caso que pediu isto: a malha não deu para o pedido inteiro, o dono
      // baixa a quantidade no card, e o cortador precisa ver o número novo. Sem
      // este listener a correção só existia na aba de quem editou — todo mundo
      // seguia cortando pelo número velho até dar F5.
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'order_metadata_items' },
        (payload) => {
          console.log('🔔 Production Core: order_metadata_items mudou', payload.eventType);
          // Só invalida (nunca refetch forçado — storm de 18/mai).
          queryClient.invalidateQueries({ queryKey: ['order-metadata-map'] });
          queryClient.invalidateQueries({ queryKey: ['order-metadata'] });
          queryClient.invalidateQueries({ queryKey: ['stock', 'order-materials'] });
          // A vendedora mudou a grade com a separação aberta: as linhas "pede N" vêm daí.
          queryClient.invalidateQueries({ queryKey: ['stock', 'saida-pecas'] });
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          console.log('✅ Production Core Sync: 15 listeners ativos em 1 channel');
        }
      });

    return () => {
      console.log('🔕 Production Core Sync: Removendo channel');
      clearTimeout(resetTimeout);
      realtime.removeChannel(channel);
    };
  }, [queryClient, playSound]);
};
